/**
 * 8x4 Plywood Cutting Layout Engine
 * First-fit decreasing height bin-packing with visual sheet maps
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Scissors, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';

const COLORS = [
  '#3b82f6','#ef4444','#22c55e','#f59e0b','#8b5cf6','#ec4899','#14b8a6',
  '#f97316','#06b6d4','#84cc16','#e11d48','#6366f1','#0ea5e9','#d946ef',
];

function packParts(parts, sheetW, sheetH, kerf) {
  const sheets = [];
  const sorted = [...parts].sort((a, b) => b.h - a.h || b.w - a.w);
  const placed = [];

  for (const part of sorted) {
    for (let c = 0; c < part.qty; c++) {
      let didPlace = false;
      for (const sheet of sheets) {
        const pos = findSpot(sheet, part.w + kerf, part.h + kerf, sheetW, sheetH);
        if (pos) {
          sheet.items.push({ ...part, x: pos.x, y: pos.y, pw: part.w, ph: part.h, copyIdx: c });
          didPlace = true;
          break;
        }
      }
      if (!didPlace) {
        const newSheet = { id: sheets.length + 1, items: [] };
        newSheet.items.push({ ...part, x: 0, y: 0, pw: part.w, ph: part.h, copyIdx: c });
        sheets.push(newSheet);
      }
    }
  }
  return sheets;
}

function findSpot(sheet, pw, ph, sheetW, sheetH) {
  const grid = Array.from({ length: Math.ceil(sheetH) }, () => new Uint8Array(Math.ceil(sheetW)));
  for (const item of sheet.items) {
    for (let y = Math.floor(item.y); y < Math.min(Math.ceil(item.y + item.ph), sheetH); y++)
      for (let x = Math.floor(item.x); x < Math.min(Math.ceil(item.x + item.pw), sheetH); x++)
        if (y < grid.length && x < grid[0].length) grid[y][x] = 1;
  }
  for (let y = 0; y <= sheetH - ph; y++) {
    for (let x = 0; x <= sheetW - pw; x++) {
      let fits = true;
      for (let dy = 0; dy < ph && fits; dy++)
        for (let dx = 0; dx < pw && fits; dx++)
          if (grid[Math.floor(y + dy)]?.[Math.floor(x + dx)]) fits = false;
      if (fits) return { x, y };
    }
  }
  return null;
}

export default function CuttingLayoutEngine({ cutList = [], calculations = {} }) {
  const [sheetWidth, setSheetWidth] = useState(96);   // 8ft in inches
  const [sheetHeight, setSheetHeight] = useState(48);  // 4ft in inches
  const [sawKerf, setSawKerf] = useState(0.15);        // inches
  const [sheetCost, setSheetCost] = useState(1856);

  // Convert cutList mm to inches, group by material
  const partsByMaterial = useMemo(() => {
    const groups = {};
    cutList.forEach((item, idx) => {
      const mat = item.material || 'Unknown';
      if (!groups[mat]) groups[mat] = [];
      groups[mat].push({
        code: item.code, part: item.part, qty: item.qty || 1,
        w: Math.round(item.w / 25.4 * 10) / 10,
        h: Math.round(item.h / 25.4 * 10) / 10,
        colorIdx: idx,
      });
    });
    return groups;
  }, [cutList]);

  const layouts = useMemo(() => {
    const result = {};
    Object.entries(partsByMaterial).forEach(([mat, parts]) => {
      result[mat] = packParts(parts, sheetWidth, sheetHeight, sawKerf);
    });
    return result;
  }, [partsByMaterial, sheetWidth, sheetHeight, sawKerf]);

  const stats = useMemo(() => {
    let totalSheets = 0, totalUsed = 0;
    const sheetArea = sheetWidth * sheetHeight;
    Object.values(layouts).forEach(sheets => {
      totalSheets += sheets.length;
      sheets.forEach(s => {
        s.items.forEach(i => { totalUsed += i.pw * i.ph; });
      });
    });
    const totalArea = totalSheets * sheetArea;
    const wastage = totalArea > 0 ? ((totalArea - totalUsed) / totalArea) * 100 : 0;
    return { totalSheets, yield: 100 - wastage, wastage, cost: totalSheets * sheetCost };
  }, [layouts, sheetWidth, sheetHeight, sheetCost]);

  const exportPDF = () => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(14);
    doc.text('Cutting Layout Report', 14, 15);
    doc.setFontSize(9);
    doc.text(`Sheet: ${sheetWidth}" x ${sheetHeight}" | Kerf: ${sawKerf}" | Sheets: ${stats.totalSheets} | Yield: ${stats.yield.toFixed(1)}%`, 14, 22);
    
    // One sheet per page to avoid page-break splits
    let isFirst = true;
    Object.entries(layouts).forEach(([mat, sheets]) => {
      sheets.forEach((sheet, si) => {
        if (!isFirst) doc.addPage();
        isFirst = false;
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        doc.setFontSize(11);
        doc.text(`${mat} — Sheet ${si + 1} of ${sheets.length}`, 14, 15);
        // Scale to fit page with margins
        const maxW = pageW - 28, maxH = pageH - 40;
        const scale = Math.min(maxW / sheetWidth, maxH / sheetHeight);
        const ox = 14, oy = 22;
        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.rect(ox, oy, sheetWidth * scale, sheetHeight * scale);
        // Dimension labels on sheet border
        doc.setFontSize(7);
        doc.text(`${sheetWidth}"`, ox + sheetWidth * scale / 2 - 5, oy - 2);
        doc.text(`${sheetHeight}"`, ox - 10, oy + sheetHeight * scale / 2);
        sheet.items.forEach(item => {
          const c = COLORS[item.colorIdx % COLORS.length];
          const r = parseInt(c.slice(1, 3), 16), g = parseInt(c.slice(3, 5), 16), b = parseInt(c.slice(5, 7), 16);
          doc.setFillColor(r, g, b);
          const rx = ox + item.x * scale, ry = oy + item.y * scale, rw = item.pw * scale, rh = item.ph * scale;
          doc.rect(rx, ry, rw, rh, 'FD');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(rw > 15 && rh > 10 ? 7 : 5);
          doc.text(item.code, rx + 2, ry + 5);
          // Show dimensions inside panel
          if (rw > 25 && rh > 15) {
            const dimText = `${Math.round(item.pw * 25.4)}x${Math.round(item.ph * 25.4)}`;
            doc.setFontSize(5);
            doc.text(dimText, rx + 2, ry + 10);
          }
          doc.setTextColor(0, 0, 0);
        });
      });
    });
    doc.save('Cutting_Layout.pdf');
  };

  return (
    <div className="space-y-5" data-testid="cutting-layout-engine">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Scissors size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">8x4 Cutting Layout Engine</h3></div>
        <Button size="sm" variant="outline" onClick={exportPDF} data-testid="export-cutting-pdf"><Download size={14} className="mr-1" />PDF Layout</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><Label>Sheet W (in)</Label><Input type="number" value={sheetWidth} onChange={e => setSheetWidth(+e.target.value)} /></div>
        <div><Label>Sheet H (in)</Label><Input type="number" value={sheetHeight} onChange={e => setSheetHeight(+e.target.value)} /></div>
        <div><Label>Saw Kerf (in)</Label><Input type="number" step="0.01" value={sawKerf} onChange={e => setSawKerf(+e.target.value)} /></div>
        <div><Label>Sheet Cost (Rs)</Label><Input type="number" value={sheetCost} onChange={e => setSheetCost(+e.target.value)} /></div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Sheets Required', value: stats.totalSheets, color: 'bg-blue-50 text-blue-800' },
          { label: 'Yield', value: stats.yield.toFixed(1) + '%', color: 'bg-green-50 text-green-800' },
          { label: 'Wastage', value: stats.wastage.toFixed(1) + '%', color: stats.wastage > 20 ? 'bg-red-50 text-red-800' : 'bg-stone-50 text-stone-800' },
          { label: 'Sheet Cost', value: `Rs ${stats.cost.toLocaleString('en-IN')}`, color: 'bg-amber-50 text-amber-800' },
        ].map((s, i) => (
          <div key={i} className={`p-3 rounded-lg ${s.color}`}>
            <p className="text-[10px] uppercase font-bold opacity-70">{s.label}</p>
            <p className="text-lg font-extrabold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Visual cutting maps */}
      {Object.entries(layouts).map(([mat, sheets]) => (
        <Card key={mat}>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm mb-3 text-stone-700">{mat} — {sheets.length} sheet{sheets.length > 1 ? 's' : ''}</h4>
            <div className="flex flex-wrap gap-4">
              {sheets.map((sheet, si) => {
                const scale = Math.min(400 / sheetWidth, 220 / sheetHeight);
                const svgW = sheetWidth * scale, svgH = sheetHeight * scale;
                return (
                  <div key={si} className="border rounded p-2 bg-stone-50">
                    <p className="text-[10px] font-bold text-stone-500 mb-1">Sheet {si + 1}</p>
                    <svg width={svgW} height={svgH} className="border border-stone-300 bg-white">
                      {sheet.items.map((item, idx) => {
                        const rx = item.x * scale, ry = item.y * scale, rw = item.pw * scale, rh = item.ph * scale;
                        const dimMM = `${Math.round(item.pw * 25.4)}x${Math.round(item.ph * 25.4)}`;
                        return (
                          <g key={idx}>
                            <rect x={rx} y={ry} width={rw} height={rh}
                              fill={COLORS[item.colorIdx % COLORS.length]} stroke="#fff" strokeWidth="1" opacity="0.85" />
                            {rw > 20 && rh > 12 && (
                              <text x={rx + 3} y={ry + 11} fontSize="9" fill="#fff" fontWeight="bold">{item.code}</text>
                            )}
                            {rw > 40 && rh > 22 && (
                              <text x={rx + 3} y={ry + 21} fontSize="7" fill="rgba(255,255,255,0.8)">{dimMM}</text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
